// modernGenderCard.jsx
import React from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Chip,
  Avatar,
  Stack,
} from '@mui/material';
import WcIcon from '@mui/icons-material/Wc';
import { 
  Cell, 
  Pie, 
  PieChart, 
  ResponsiveContainer, 
  Tooltip,
} from 'recharts';
import { alpha, useTheme } from '@mui/material/styles';

const GENDER_COLORS = {
  Male: '#60A5FA',
  Female: '#F9A8D4',
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    const total = payload[0].payload.total || 100;
    const percentage = ((data.value / total) * 100).toFixed(1);
    
    return (
        <Box
          sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          border: '1px solid #E5E7EB',
          borderRadius: 2,
          p: 1.5,
          boxShadow: '0 8px 20px rgba(15,23,42,0.08)',
        }}
      >
        <Typography variant="body2" fontWeight={600}>
          {data.name}
        </Typography>
        <Typography variant="body2" sx={{ color: '#111827' }} fontWeight={700}>
          {percentage}%
        </Typography>
      </Box>
    );
  }
  return null;
};

export default function ModernGenderCard({ data = [] }) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  if (!data || data.length === 0) return null;

  const processedData = data.filter(item => item.value > 0);
  if (processedData.length === 0) return null;

  const total = processedData.reduce((sum, item) => sum + item.value, 0);
  const dataWithTotal = processedData.map(item => ({ ...item, total }));

  return (
    <Card 
      elevation={0}
      sx={{ 
        height: '100%',
        borderRadius: 3,
        border: `1px solid ${isDarkMode ? '#2A2A2A' : '#E5E7EB'}`,
        backgroundColor: isDarkMode ? '#1A1A1A' : '#FFFFFF',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: isDarkMode
            ? '0 8px 20px rgba(0,0,0,0.3)'
            : '0 8px 20px rgba(15,23,42,0.06)',
        }
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              width: 34,
              height: 34,
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : '#F3F4F6',
              color: isDarkMode ? '#D1D5DB' : '#6B7280',
              mr: 2,
            }}
          >
            <WcIcon sx={{ fontSize: 16 }} />
          </Avatar>
          <Typography 
            variant="h6" 
            fontWeight={700}
            color="text.primary"
          >
            Gender Split
          </Typography>
        </Box>

        <Box sx={{ height: 170, mb: 1.5 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={dataWithTotal}
                cx="50%"
                cy="50%"
                innerRadius="40%"
                outerRadius="70%"
                dataKey="value"
                stroke="none"
              >
                {dataWithTotal.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={GENDER_COLORS[entry.name] || theme.palette.primary.main}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </Box>
        
        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
          {dataWithTotal.map((entry) => {
            const percentage = ((entry.value / total) * 100).toFixed(0);
            return (
              <Chip
                key={entry.name}
                label={`${entry.name} ${percentage}%`}
                size="small"
                sx={{
                  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : '#F8FAFC',
                  color: isDarkMode ? '#FFFFFF' : '#111827',
                  fontWeight: 600,
                  border: `1px solid ${isDarkMode ? '#343434' : '#E5E7EB'}`,
                }}
              />
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
}
